import { memo } from "react";

import TableBodyRow from "ds/components/Table/Row";
import TableCell from "ds/components/Table/Row/Cell";
import useRowKeyboardNavigation from "ds/components/Table/useKeyboardNavigation/useRowKeyboardNavigation";
import { Context } from "types/generated";

import useContextsTableStore from "../useContextsTableStore";
import { selectVisibleColumns } from "../useContextsTableStore/selectors";
import { CONTEXTS_COLUMNS_WITH_ACTIONS, ContextsColumnWithActions } from "./useCellActionsConfig";
import ContextsListItemDescriptionCell from "./DescriptionCell";
import ContextsListItemSpaceCell from "./SpaceCell";
import ContextListItemCreatedAtCell from "./CreatedAtCell";
import ContextsListItemLabelsCell from "./LabelsCell";
import ContextsListItemNameCell from "./NameCell";
import ContextsListTableDropdownCell from "./TableDropdownCell";
import ContextsListItemActionsDropdownCell from "./DropdownActionsCell";
import { CONTEXTS_COLUMN_ID, ROW_HEADER_ID } from "../../../constants";

const CELLS = {
  [CONTEXTS_COLUMN_ID.DESCRIPTION]: ContextsListItemDescriptionCell,
  [CONTEXTS_COLUMN_ID.NAME]: ContextsListItemNameCell,
  [CONTEXTS_COLUMN_ID.SPACE]: ContextsListItemSpaceCell,
  [CONTEXTS_COLUMN_ID.CREATED_AT]: ContextListItemCreatedAtCell,
  [CONTEXTS_COLUMN_ID.LABELS]: ContextsListItemLabelsCell,
  [CONTEXTS_COLUMN_ID.DROPDOWN]: ContextsListItemActionsDropdownCell,
};

type ContextRowProps = {
  context: Context;
};

const ContextRow = (props: ContextRowProps) => {
  const { context } = props;
  const columns = useContextsTableStore((state) => selectVisibleColumns(state));
  const keyboardProps = useRowKeyboardNavigation({ isGroup: false });

  return (
    <TableBodyRow {...keyboardProps}>
      {columns.map(({ id }) => {
        const Component = CELLS[id as CONTEXTS_COLUMN_ID];
        const hasActions = CONTEXTS_COLUMNS_WITH_ACTIONS.includes(id as ContextsColumnWithActions);

        const uniqueKey = `${id}-${context.id}`;

        const childrenComponent = <Component context={context} />;

        return hasActions ? (
          <ContextsListTableDropdownCell
            key={uniqueKey}
            id={id as ContextsColumnWithActions}
            context={context}
          >
            {childrenComponent}
          </ContextsListTableDropdownCell>
        ) : (
          <TableCell
            key={uniqueKey}
            id={id}
            stickyLeft={id === CONTEXTS_COLUMN_ID.NAME}
            stickyRight={id === CONTEXTS_COLUMN_ID.DROPDOWN}
            role={id === ROW_HEADER_ID ? "rowheader" : undefined}
            align="center"
          >
            {childrenComponent}
          </TableCell>
        );
      })}
    </TableBodyRow>
  );
};

export default memo(ContextRow);
